import { supabase } from '../config/supabase'
import userService from './userService'

class AdminService {
  constructor() {
    // Admin emails for initial setup and fallback
    this.adminEmails = ['admin@logislip.com', 'support@logislip.com']
  }

  // Check if current user is admin
  isAdmin() {
    const user = userService.getCurrentUser()
    if (!user) return false
    
    // Primary check: user role
    if (user.role === 'admin') return true
    
    // Fallback check: admin email list (for backward compatibility)
    return this.adminEmails.includes(user.email.toLowerCase())
  }

  // Get dashboard statistics
  async getDashboardStats() {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      // Get user counts by subscription tier
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('subscription_tier, subscription_status, created_at, usage_count, usage_limit')
        .order('created_at', { ascending: false })

      if (usersError) throw usersError

      // Get payment data for revenue calculation
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('amount, currency, status, billing_cycle, created_at, plan_id')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })

      if (paymentsError) throw paymentsError

      // Calculate statistics
      const totalUsers = users.length
      const freeUsers = users.filter(u => u.subscription_tier === 'free').length
      const paidUsers = users.filter(u => u.subscription_tier !== 'free' && u.subscription_status === 'active').length
      
      // Calculate total revenue (assuming all payments are in INR)
      const totalRevenue = payments.reduce((sum, payment) => {
        return sum + (parseFloat(payment.amount) || 0)
      }, 0)

      // Monthly recurring revenue calculation
      const currentMonth = new Date().getMonth()
      const currentYear = new Date().getFullYear()
      const thisMonthPayments = payments.filter(p => {
        const paymentDate = new Date(p.created_at)
        return paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear
      })
      const monthlyRevenue = thisMonthPayments.reduce((sum, payment) => {
        return sum + (parseFloat(payment.amount) || 0)
      }, 0)

      // Growth calculations
      const last30Days = new Date()
      last30Days.setDate(last30Days.getDate() - 30)
      
      const newUsersLast30Days = users.filter(u => new Date(u.created_at) >= last30Days).length
      
      // Subscription tier breakdown
      const subscriptionBreakdown = users.reduce((acc, user) => {
        const tier = user.subscription_tier || 'free'
        acc[tier] = (acc[tier] || 0) + 1
        return acc
      }, {})

      // Average usage statistics
      const activeUsers = users.filter(u => u.usage_count > 0)
      const avgUsage = activeUsers.length > 0 
        ? activeUsers.reduce((sum, u) => sum + u.usage_count, 0) / activeUsers.length 
        : 0

      return {
        totalUsers,
        freeUsers,
        paidUsers,
        totalRevenue,
        monthlyRevenue,
        newUsersLast30Days,
        subscriptionBreakdown,
        avgUsage: Math.round(avgUsage * 100) / 100,
        recentUsers: users.slice(0, 10),
        recentPayments: payments.slice(0, 10)
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
      throw error
    }
  }

  // Get all users with pagination and filters
  async getUsers({ page = 1, limit = 50, search = '', tier = '', status = '' } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      let query = supabase
        .from('users')
        .select(`
          id, 
          name, 
          email, 
          avatar_url, 
          phone_number, 
          phone_verified,
          role,
          subscription_tier, 
          subscription_status, 
          subscription_end_date,
          usage_count, 
          usage_limit,
          created_at,
          last_login
        `, { count: 'exact' })

      // Apply search filter
      if (search) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%`)
      }

      // Apply tier filter
      if (tier) {
        query = query.eq('subscription_tier', tier)
      }

      // Apply status filter
      if (status) {
        query = query.eq('subscription_status', status)
      }

      // Apply pagination
      const offset = (page - 1) * limit
      query = query.range(offset, offset + limit - 1).order('created_at', { ascending: false })

      const { data: users, error, count } = await query

      if (error) throw error

      return {
        users,
        totalCount: count,
        currentPage: page,
        totalPages: Math.ceil(count / limit),
        hasNextPage: page * limit < count,
        hasPrevPage: page > 1
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      throw error
    }
  }

  // Update user subscription
  async updateUserSubscription(userId, subscriptionData) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const updateData = {
        subscription_tier: subscriptionData.tier,
        subscription_status: subscriptionData.status,
        subscription_end_date: subscriptionData.endDate,
        usage_limit: subscriptionData.usageLimit,
        updated_at: new Date().toISOString()
      }

      // Add role update if provided
      if (subscriptionData.role) {
        updateData.role = subscriptionData.role
      }

      const { data: updatedUser, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single()

      if (error) throw error

      // Log admin action
      await this.logAdminAction('user_subscription_updated', {
        userId,
        subscriptionData
      })

      return updatedUser
    } catch (error) {
      console.error('Error updating user subscription:', error)
      throw error
    }
  }

  // Delete user account
  async deleteUser(userId) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId)

      if (error) throw error

      // Log admin action
      await this.logAdminAction('user_deleted', { userId })

      return { success: true }
    } catch (error) {
      console.error('Error deleting user:', error)
      throw error
    }
  }

  // Get subscription plans
  async getSubscriptionPlans() {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      // For now, return the plans from subscription service
      // In the future, this could come from a database table
      const subscriptionService = (await import('./subscriptionService')).default
      return subscriptionService.getSubscriptionPlans()
    } catch (error) {
      console.error('Error fetching subscription plans:', error)
      throw error
    }
  }

  // Get payment history with analytics
  async getPayments({ page = 1, limit = 50, status = '', planId = '' } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      let query = supabase
        .from('payments')
        .select(`
          id,
          user_id,
          plan_id,
          billing_cycle,
          amount,
          currency,
          status,
          payment_method,
          created_at,
          completed_at,
          users!inner(name, email)
        `, { count: 'exact' })

      // Apply filters
      if (status) {
        query = query.eq('status', status)
      }

      if (planId) {
        query = query.eq('plan_id', planId)
      }

      // Apply pagination
      const offset = (page - 1) * limit
      query = query.range(offset, offset + limit - 1).order('created_at', { ascending: false })

      const { data: payments, error, count } = await query

      if (error) throw error

      return {
        payments,
        totalCount: count,
        currentPage: page,
        totalPages: Math.ceil(count / limit),
        hasNextPage: page * limit < count,
        hasPrevPage: page > 1
      }
    } catch (error) {
      console.error('Error fetching payments:', error)
      throw error
    }
  }

  // Get usage analytics
  async getUsageAnalytics() {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      // Get usage logs
      const { data: usageLogs, error: logsError } = await supabase
        .from('usage_logs')
        .select('action, resource_type, created_at, user_id')
        .order('created_at', { ascending: false })
        .limit(1000)

      if (logsError) throw logsError

      // Get resource usage data
      const { data: resourceUsage, error: resourceError } = await supabase
        .from('resource_usage')
        .select('user_id, resource_type, current_count, limit_count')

      if (resourceError) throw resourceError

      // Process analytics
      const actionCounts = usageLogs.reduce((acc, log) => {
        acc[log.action] = (acc[log.action] || 0) + 1
        return acc
      }, {})

      const resourceCounts = resourceUsage.reduce((acc, usage) => {
        if (!acc[usage.resource_type]) {
          acc[usage.resource_type] = { total: 0, used: 0 }
        }
        acc[usage.resource_type].total += usage.limit_count
        acc[usage.resource_type].used += usage.current_count
        return acc
      }, {})

      return {
        actionCounts,
        resourceCounts,
        totalActions: usageLogs.length,
        activeUsers: new Set(usageLogs.map(log => log.user_id)).size
      }
    } catch (error) {
      console.error('Error fetching usage analytics:', error)
      throw error
    }
  }

  // Log admin actions for audit trail
  async logAdminAction(action, details = {}) {
    const adminUser = userService.getCurrentUser()
    if (!adminUser) return

    try {
      await supabase
        .from('usage_logs')
        .insert({
          user_id: adminUser.id,
          action: `admin_${action}`,
          resource_type: 'admin',
          details: {
            ...details,
            admin_email: adminUser.email,
            timestamp: new Date().toISOString()
          }
        })
    } catch (error) {
      console.error('Error logging admin action:', error)
    }
  }

  // Bulk operations
  async bulkUpdateUserTier(userIds, newTier) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const { data: updatedUsers, error } = await supabase
        .from('users')
        .update({
          subscription_tier: newTier,
          updated_at: new Date().toISOString()
        })
        .in('id', userIds)
        .select()

      if (error) throw error

      await this.logAdminAction('bulk_tier_update', {
        userIds,
        newTier,
        count: updatedUsers.length
      })

      return updatedUsers
    } catch (error) {
      console.error('Error bulk updating user tiers:', error)
      throw error
    }
  }

  // Update user role (promote/demote admin)
  async updateUserRole(userId, newRole) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    if (!['user', 'admin'].includes(newRole)) {
      throw new Error('Invalid role. Must be either "user" or "admin"')
    }

    try {
      const { data: updatedUser, error } = await supabase
        .from('users')
        .update({
          role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single()

      if (error) throw error

      await this.logAdminAction('user_role_updated', {
        userId,
        newRole,
        previousRole: updatedUser.role
      })

      return updatedUser
    } catch (error) {
      console.error('Error updating user role:', error)
      throw error
    }
  }

  // Export users data for analysis
  async exportUsersData() {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      await this.logAdminAction('users_data_exported', {
        count: users.length
      })

      return users
    } catch (error) {
      console.error('Error exporting users data:', error)
      throw error
    }
  }
}

// Create singleton instance
const adminService = new AdminService()
export default adminService